import { Box, Flex, Text } from '@chakra-ui/react';
import {
  METRIC_ROWS,
  type ArmMatch,
  type ArmView,
  type ComparisonView,
  type MetricKey,
  type MonitorView,
  type SnapshotState,
  type WindowView
} from './model';

const ABSENT = '\u2014';

const STATE_COPY: Record<SnapshotState, string | null> = {
  fresh: null,
  missing: 'No snapshot yet',
  rate_limited: 'Rate limited',
  credential_error: 'Credential error',
  error: 'Couldn’t read this snapshot'
};

function formatCount(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
}

function formatPlain(value: number): string {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

function formatMoney(value: number, currency: string | null): string {
  if (currency) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
    } catch {
      return `${formatPlain(value)} ${currency}`;
    }
  }
  return formatPlain(value);
}

function formatRoas(value: number): string {
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 }).format(value)}×`;
}

function formatMetric(key: MetricKey, value: number, currency: string | null): string {
  if (key === 'spend' || key === 'purchaseValue') return formatMoney(value, currency);
  if (key === 'roas') return formatRoas(value);
  return formatCount(value);
}

function formatTaken(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const formatted = new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'UTC'
  }).format(date);
  return `${formatted} UTC`;
}

function stateColor(state: SnapshotState): string | undefined {
  if (state === 'rate_limited' || state === 'credential_error' || state === 'error') return 'red.700';
  if (state === 'missing') return 'orange.700';
  return undefined;
}

function counts(arm: ArmView, key: MetricKey): boolean {
  return arm.match !== 'unmatched' && arm.metrics[key] != null;
}

function rowsFor(comparison: ComparisonView): typeof METRIC_ROWS[number][] {
  return METRIC_ROWS.filter((row) => counts(comparison.ours, row.key) || counts(comparison.control, row.key));
}

function metricCell(arm: ArmView, key: MetricKey, currency: string | null): string {
  if (arm.match === 'unmatched') return ABSENT;
  const value = arm.metrics[key];
  if (value == null) return ABSENT;
  return formatMetric(key, value, arm.currency ?? currency);
}

function MetricTable({ comparison, currency }: { comparison: ComparisonView; currency: string | null }) {
  const rows = rowsFor(comparison);
  if (!rows.length) return null;
  return (
    <Box as="table" w="100%" fontSize="12px" sx={{ borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <Text as="th" textAlign="left" py="2px" fontWeight="600" color="gray.500" />
          <Text as="th" textAlign="right" py="2px" fontWeight="600">Ours</Text>
          <Text as="th" textAlign="right" py="2px" fontWeight="600">Control</Text>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <Text as="td" py="2px" color="gray.600">{row.label}</Text>
            <Text as="td" py="2px" textAlign="right" fontFamily="mono">{metricCell(comparison.ours, row.key, currency)}</Text>
            <Text as="td" py="2px" textAlign="right" fontFamily="mono">{metricCell(comparison.control, row.key, currency)}</Text>
          </tr>
        ))}
      </tbody>
    </Box>
  );
}

function armLine(side: 'Ours' | 'Control', arm: ArmView): string | null {
  if (arm.match === 'matched') return arm.detail ? `${side}: ${arm.detail}` : null;
  const label: Record<Exclude<ArmMatch, 'matched'>, string> = {
    unmatched: 'unmatched',
    ambiguous: 'ambiguous'
  };
  const base = `${side} arm ${label[arm.match]}`;
  return arm.detail ? `${base}: ${arm.detail}` : base;
}

function totalsLine(window: WindowView): string | null {
  const parts: string[] = [];
  if (window.campaigns != null) parts.push(`${formatCount(window.campaigns)} campaigns`);
  if (window.live != null) parts.push(`${formatCount(window.live)} live`);
  const money: Array<[MetricKey, string]> = [
    ['spend', 'spend'],
    ['purchaseValue', 'purchase value'],
    ['roas', 'ROAS']
  ];
  for (const [key, label] of money) {
    const value = window.totals[key];
    if (value == null) continue;
    parts.push(`${label} ${formatMetric(key, value, window.currency)}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

function rangeText(window: WindowView): string | null {
  if (window.since && window.until) return `${window.since} – ${window.until}`;
  return window.since ?? window.until;
}

function WindowBlock({ window }: { window: WindowView }) {
  const taken = formatTaken(window.takenAt);
  const range = rangeText(window);
  const totals = totalsLine(window);
  const bare = window.comparisons.length === 0 && !window.takenAt;
  return (
    <Box data-testid="campaign-monitor-window" pt="8px">
      <Flex justify="space-between" gap="8px" wrap="wrap" mb="4px">
        <Text fontSize="12px" fontWeight="700">
          {window.label}
          {range ? ` · ${range}` : ''}
        </Text>
        <Text fontSize="11px" color="gray.500" data-testid="campaign-monitor-taken">
          {taken ? `Snapshot ${taken}` : ''}
        </Text>
      </Flex>
      {window.currency ? (
        <Text fontSize="11px" color="gray.500" data-testid="campaign-monitor-currency">{window.currency}</Text>
      ) : null}
      {totals ? <Text fontSize="11px" color="gray.600" mb="4px">{totals}</Text> : null}
      {bare ? <Text fontSize="12px" color="orange.700">No snapshot yet</Text> : null}
      {window.comparisons.map((comparison, index) => {
        const oursLine = armLine('Ours', comparison.ours);
        const controlLine = armLine('Control', comparison.control);
        return (
          <Box key={`${comparison.label}-${index}`} data-testid="campaign-monitor-test" mt="8px">
            <Text fontSize="13px" fontWeight="700">{comparison.label}</Text>
            {oursLine ? <Text data-testid="campaign-monitor-arm" fontSize="12px" color={comparison.ours.match === 'matched' ? 'gray.600' : 'orange.700'}>{oursLine}</Text> : null}
            {controlLine ? <Text data-testid="campaign-monitor-arm" fontSize="12px" color={comparison.control.match === 'matched' ? 'gray.600' : 'orange.700'}>{controlLine}</Text> : null}
            <MetricTable comparison={comparison} currency={window.currency} />
          </Box>
        );
      })}
    </Box>
  );
}

/** The comparison body. The shell around it owns the title. Read-only. */
export function CampaignMonitorBody({ view }: { view: MonitorView }) {
  if (!view.recognized) {
    const version = view.version == null ? 'an unknown contract' : `contract version ${view.version}`;
    return (
      <Text data-testid="campaign-monitor-fallback" fontSize="12px" color="gray.600">
        This report uses {version}, which this app does not render yet.
      </Text>
    );
  }
  const banner = STATE_COPY[view.state];
  const showMessage = view.state === 'rate_limited' || view.state === 'credential_error' || view.state === 'error';
  return (
    <Box>
      {banner ? (
        <Text data-testid="campaign-monitor-status" fontSize="12px" color={stateColor(view.state)}>{banner}</Text>
      ) : null}
      {showMessage && view.message ? (
        <Text data-testid="campaign-monitor-message" fontSize="12px" color="red.700" mt="4px">{view.message}</Text>
      ) : null}
      {view.windows.length === 0 ? (
        view.state === 'missing' ? null : <Text fontSize="12px" color="orange.700">No snapshot yet</Text>
      ) : view.windows.map((window) => (
        <WindowBlock key={window.key ?? window.label} window={window} />
      ))}
    </Box>
  );
}
